import type { Infer } from 'superstruct';
import {
	array,
	boolean,
	nullable,
	number,
	object,
	optional,
	partial,
	string,
	union,
} from 'superstruct';

export const CANADA_POSTAL_CODE_REGEX =
	/^([A-Z][0-9][A-Z]) ?([0-9][A-Z][0-9])$/u;
export const US_ZIP_CODE_REGEX = /^([0-9]{5})(?:-?([0-9]{4}))?$/u;

export const GROUP_LEVEL_PACING: readonly string[] = ['NEAG', 'IAC'] as const;

export const OpportunityLineItemRecord = object({
	Id: string(),
	Name: string(),
	OpportunityId: optional(string()),
	ProductCode: optional(string()),
	ON_Spend2__c: optional(number()),
	Daily_Spend__c: optional(number()),
	ON_Platform_Campaign_Name_Auto__c: string(),
	Campaign_Id__c: optional(string()),
	PCID__c: optional(union([string(), number()])),
	ON_Program_Type__c: optional(string()),
	ON_Brands2__c: optional(string()),
	ON_Segment__c: optional(string()),
	ON_Group__c: optional(string()),
	ON_Campaign_Name__c: optional(string()),
	ON_Run_Start_Date__c: optional(string()),
	ON_Run_End_Date__c: optional(string()),
	Opportunity: optional(
		partial(
			object({
				AVB_ID__c: string(),
				Name: string(),
				Account: optional(
					partial(
						object({
							Name: string(),
						})
					)
				),
			})
		)
	),
});

export const OpportunityRecord = object({
	Id: string(),
	Name: string(),
	AVB_ID__c: optional(string()),
	OpportunityLineItems: object({
		records: array(OpportunityLineItemRecord),
	}),
});

export const LocationRecord = object({
	Id: string(),
	AVB_ID__c: string(),
	Name: string(),
	Address__c: string(),
	Street__c: string(),
	City__c: string(),
	State__c: string(),
	Zip_Code__c: string(),
	Marketing_Location_Targeting__c: string(),
	Active_Location__c: boolean(),
});

export const TargetingRecord = object({
	Id: string(),
	Account__c: string(),
	Name: string(),
	Addresses__c: string(),
	Radius_Miles__c: number(),
	Cities__c: string(),
	Zip_Codes__c: string(),
	Additional_Notes__c: string(),
	Overall_Targeting_Details__c: string(),
	Targeting_Details__c: string(),
	Primary_Targeting__c: boolean(),
});

export const AccountRecord = object({
	Id: string(),
	AVB_ID__c: string(),
	Name: string(),
	Website: optional(string()),
	Facebook_Page_Name__c: optional(string()),
	MD_Facebook_Link__c: optional(string()),
	MD_Google_Link__c: optional(string()),
});

export const OpportunityTranslatedToFacebookTemplate = object({
	templateName: string(), // ON_Platform_Campaign_Name_Auto__c
	templateAdAccounts: array(
		object({
			avbId: string(),
			name: string(), // list of avbIds
		})
	),
	adSetBudgets: array(
		object({
			avbId: string(),
			amount: number(), // ON_Spend2__c
		})
	),
	adSetLocations: array(
		object({
			avbId: string(),
			address: string(), // Location__c.Address__c
			city: string(), // Location__c.City__c
			state: string(), // Location__c.State__c
			zip: string(), // Location__c.Zip__c
			marketingLocation: string(), // Location__c.Marketing_Location_Targeting__c
		})
	),
	adSetName: string(), // ON_Platform_Campaign_Name_Auto__c
	campaignName: string(), // ON_Platform_Campaign_Name_Auto__c
	adSetBudgetStartDate: string(), // ON_Run_Start_Date__c
	adSetBudgetEndDate: string(), // ON_Run_End_Date__c
});

export const SalesforceImportPreviewAdAccount = object({
	avbId: string(),
	name: optional(string()),
	dailySpend: nullable(number()),
});

export const SalesforceImportPreviewRow = object({
	id: string(),
	templateName: string(),
	platformCampaignName: string(),
	programTypes: array(string()),
	brands: array(string()),
	adAccounts: array(SalesforceImportPreviewAdAccount),
	runStartDate: string(),
	runEndDate: string(),
	productCodes: array(string()),
	importableFacebook: boolean(),
	importableGoogle: boolean(),
});

export type OpportunityRecord = Infer<typeof OpportunityRecord>;
export type OpportunityLineItemRecord = Infer<typeof OpportunityLineItemRecord>;
export interface SalesforceImportCampaignLink {
	opportunityLineItemId: string;
	opportunityId?: string;
	avbId: string;
	markerId: string;
	productCode: string;
	sourceCampaignName: string;
}
export type LocationRecord = Infer<typeof LocationRecord>;
export type TargetingRecord = Infer<typeof TargetingRecord>;
export type AccountRecord = Infer<typeof AccountRecord>;
export type OpportunityTranslatedToFacebookTemplate = Infer<
	typeof OpportunityTranslatedToFacebookTemplate
>;
export type SalesforceImportPreviewAdAccount = Infer<
	typeof SalesforceImportPreviewAdAccount
>;
export type SalesforceImportPreviewRow = Infer<
	typeof SalesforceImportPreviewRow
>;
